import React from 'react';
import { Box, Container, Stack } from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { hasAccountingAccess } from '../../subscription';
import {
    allocateBankTransaction,
    allocatePaymentProviderTransaction,
    createCustomer,
    createExpense,
    createIncome,
    createSupplier,
} from '../../api';
import {
    buildCustomerDisplayName,
    findMatchingCustomerOption,
} from '../shared/customerSelectUtils';
import {
    buildSupplierDisplayName,
    findMatchingSupplierOption,
} from '../shared/supplierSelectUtils';
import AssignedBankTransactionsCard from './assignedTransactions/AssignedBankTransactionsCard';
import AssignedPaymentTransactionsCard from './assignedTransactions/AssignedPaymentTransactionsCard';
import AssignedTransactionsHeader from './assignedTransactions/AssignedTransactionsHeader';
import AssignedTransactionsUpgradeNotice from './assignedTransactions/AssignedTransactionsUpgradeNotice';
import BankAssignDialog from './assignedTransactions/BankAssignDialog';
import PaymentAssignDialog from './assignedTransactions/PaymentAssignDialog';
import useAllocationReferenceData from './assignedTransactions/useAllocationReferenceData';
import useAssignedTransactionsData from './assignedTransactions/useAssignedTransactionsData';
import {
    buildAllocationRow,
    buildCustomerPayload,
    buildExpensePayload,
    buildIncomePayload,
    buildSupplierPayload,
    parseAmount,
    toCurrencyString,
    toDateInputValue,
} from './assignedTransactions/utils';

export default function AssignedTransactions() {
    const { user } = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);

    const {
        bankTransactions,
        bankLoading,
        bankError,
        paymentTransactions,
        paymentLoading,
        paymentError,
        refresh,
    } = useAssignedTransactionsData(hasAccounting);

    const [bankAssignDialogOpen, setBankAssignDialogOpen] = React.useState(false);
    const [activeBankTransaction, setActiveBankTransaction] = React.useState(null);
    const [bankAssigning, setBankAssigning] = React.useState(false);
    const [bankAssignError, setBankAssignError] = React.useState('');
    const [bankAllocationRows, setBankAllocationRows] = React.useState([]);
    const [bankAllocationErrors, setBankAllocationErrors] = React.useState({});
    const [bankCreatingCustomer, setBankCreatingCustomer] = React.useState(false);
    const [bankCreatingSupplier, setBankCreatingSupplier] = React.useState(false);
    const [bankComment, setBankComment] = React.useState('');

    const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
    const [activeTransaction, setActiveTransaction] = React.useState(null);
    const [allocationRows, setAllocationRows] = React.useState([]);
    const [assigning, setAssigning] = React.useState(false);
    const [assignError, setAssignError] = React.useState('');
    const [allocationErrors, setAllocationErrors] = React.useState({});

    const {
        referenceData,
        referenceLoading,
        customerOptions,
        supplierOptions,
        loadReferenceData,
        appendCustomer,
        appendSupplier,
    } = useAllocationReferenceData();
    const [creatingCustomer, setCreatingCustomer] = React.useState(false);
    const [creatingSupplier, setCreatingSupplier] = React.useState(false);

    React.useEffect(() => {
        if (!bankAssignDialogOpen || referenceData.invoices.length === 0) {
            return;
        }
        setBankAllocationRows((prev) => {
            if (prev.length === 0) {
                return prev;
            }
            const row = prev[0];
            if (row.allocationType !== 'INVOICE' || row.targetId) {
                return prev;
            }
            const defaultInvoiceId = activeBankTransaction?.matchedInvoiceId
                || referenceData.invoices[0]?.id
                || '';
            if (!defaultInvoiceId) {
                return prev;
            }
            return [
                {
                    ...row,
                    targetId: defaultInvoiceId,
                },
                ...prev.slice(1),
            ];
        });
    }, [referenceData.invoices, bankAssignDialogOpen, activeBankTransaction]);

    const openAssignDialog = (transaction) => {
        if (!transaction) {
            return;
        }
        setActiveTransaction(transaction);
        setAssignError('');
        setCreatingCustomer(false);
        setCreatingSupplier(false);
        setAllocationErrors({});

        if (transaction.allocations && transaction.allocations.length > 0) {
            const rows = transaction.allocations.map(alloc => (
                buildAllocationRow({
                    allocationType: alloc.allocationType,
                    targetId: alloc.targetId,
                    amount: toCurrencyString(Math.abs(Number(alloc.amount))),
                    paymentDate: toDateInputValue(alloc.paymentDate),
                })
            ));
            setAllocationRows(rows);
        } else {
            const transactionDate = toDateInputValue(transaction.transactionDateTime);
            const defaultAmount = toCurrencyString(Math.abs(Number(transaction.remainingAmount ?? transaction.netAmount ?? 0)));
            const providerId = transaction.providerTransactionId || '';
            setAllocationRows([
                buildAllocationRow({
                    allocationType: transaction.netAmount != null && Number(transaction.netAmount) < 0 ? 'EXPENSE' : 'INVOICE',
                    amount: defaultAmount,
                    paymentDate: transactionDate,
                    newIncome: {
                        invoiceDate: transactionDate,
                        invoiceNumber: providerId,
                        customerSearch: transaction.name || '',
                        grossAmount: defaultAmount,
                        netAmount: defaultAmount,
                        vatAmount: '0',
                    },
                    newExpense: {
                        invoiceDate: transactionDate,
                        invoiceNumber: providerId,
                        supplierName: transaction.name || '',
                        grossAmount: defaultAmount,
                        netAmount: defaultAmount,
                        vatAmount: '0',
                    },
                }),
            ]);
        }
        setAssignDialogOpen(true);
        loadReferenceData(transaction.transactionDateTime);
    };

    const openBankAssignDialog = (transaction) => {
        if (!transaction) {
            return;
        }
        setActiveBankTransaction(transaction);
        setBankAssignError('');
        setBankCreatingCustomer(false);
        setBankCreatingSupplier(false);
        setBankComment(transaction.comment || '');
        setBankAllocationErrors({});

        if (transaction.allocations && transaction.allocations.length > 0) {
            const rows = transaction.allocations.map(alloc => (
                buildAllocationRow({
                    allocationType: alloc.allocationType,
                    targetId: alloc.targetId,
                    amount: toCurrencyString(Math.abs(Number(alloc.amount))),
                    paymentDate: toDateInputValue(alloc.paymentDate),
                })
            ));
            setBankAllocationRows(rows);
        } else {
            const bookingDate = toDateInputValue(transaction.bookingDate || transaction.valueDate);
            const defaultAmount = toCurrencyString(Math.abs(Number(transaction.amount ?? 0)));
            const isExpense = Number(transaction.amount ?? 0) < 0;
            const defaultType = isExpense ? 'EXPENSE' : 'INVOICE';
            const defaultInvoiceId = transaction.matchedInvoiceId || referenceData.invoices?.[0]?.id || '';
            setBankAllocationRows([
                buildAllocationRow({
                    allocationType: defaultType,
                    targetId: defaultType === 'INVOICE' ? defaultInvoiceId : '',
                    amount: defaultAmount,
                    paymentDate: bookingDate,
                    newIncome: {
                        invoiceDate: bookingDate,
                        invoiceNumber: transaction.externalId || '',
                        customerSearch: transaction.counterpartName || '',
                        grossAmount: defaultAmount,
                        netAmount: defaultAmount,
                        vatAmount: '0',
                    },
                    newExpense: {
                        invoiceDate: bookingDate,
                        invoiceNumber: transaction.externalId || '',
                        supplierName: transaction.counterpartName || '',
                        grossAmount: defaultAmount,
                        netAmount: defaultAmount,
                        vatAmount: '0',
                    },
                }),
            ]);
        }

        setBankAssignDialogOpen(true);
        loadReferenceData(transaction.bookingDate || transaction.valueDate);
    };

    const closeAssignDialog = () => {
        setAssignDialogOpen(false);
        setActiveTransaction(null);
        setAllocationRows([]);
        setAssignError('');
        setCreatingCustomer(false);
        setCreatingSupplier(false);
        setAllocationErrors({});
    };

    const closeBankAssignDialog = () => {
        setBankAssignDialogOpen(false);
        setActiveBankTransaction(null);
        setBankAssignError('');
        setBankAllocationRows([]);
        setBankAllocationErrors({});
        setBankCreatingCustomer(false);
        setBankCreatingSupplier(false);
        setBankComment('');
    };

    const clearRowError = (setter, rowId, field) => {
        setter((prev) => {
            const rowErrors = prev?.[rowId];
            if (!rowErrors || !rowErrors[field]) {
                return prev;
            }
            const nextRow = { ...rowErrors };
            delete nextRow[field];
            const next = { ...prev };
            if (Object.keys(nextRow).length > 0) {
                next[rowId] = nextRow;
            } else {
                delete next[rowId];
            }
            return next;
        });
    };

    const setRowError = (setter, rowId, field, message) => {
        setter((prev) => ({
            ...(prev || {}),
            [rowId]: {
                ...(prev?.[rowId] || {}),
                [field]: message,
            },
        }));
    };

    const clearAllocationError = (rowId, field) => clearRowError(setAllocationErrors, rowId, field);
    const clearBankAllocationError = (rowId, field) => clearRowError(setBankAllocationErrors, rowId, field);
    const setAllocationRowError = (rowId, field, message) =>
        setRowError(setAllocationErrors, rowId, field, message);
    const setBankAllocationRowError = (rowId, field, message) =>
        setRowError(setBankAllocationErrors, rowId, field, message);

    const updateAllocationRow = (id, patch) => {
        Object.keys(patch || {}).forEach((field) => clearAllocationError(id, field));
        setAllocationRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
        );
    };

    const updateAllocationNested = (id, key, patch) => {
        Object.keys(patch || {}).forEach((field) => clearAllocationError(id, field));
        setAllocationRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                return {
                    ...row,
                    [key]: {
                        ...row[key],
                        ...patch,
                    },
                };
            })
        );
    };

    const updateBankAllocationRow = (id, patch) => {
        Object.keys(patch || {}).forEach((field) => clearBankAllocationError(id, field));
        setBankAllocationRows((prev) =>
            prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
        );
    };

    const updateBankAllocationNested = (id, key, patch) => {
        Object.keys(patch || {}).forEach((field) => clearBankAllocationError(id, field));
        setBankAllocationRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                return {
                    ...row,
                    [key]: {
                        ...row[key],
                        ...patch,
                    },
                };
            })
        );
    };

    const addAllocationRow = () => {
        if (!activeTransaction) {
            return;
        }
        setAllocationRows((prev) => [
            ...prev,
            buildAllocationRow({
                allocationType: 'INVOICE',
                paymentDate: toDateInputValue(activeTransaction.transactionDateTime),
            }),
        ]);
    };

    const removeAllocationRow = (id) => {
        setAllocationRows((prev) => prev.filter((row) => row.id !== id));
        setAllocationErrors((prev) => {
            if (!prev?.[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const addBankAllocationRow = () => {
        if (!activeBankTransaction) {
            return;
        }
        setBankAllocationRows((prev) => [
            ...prev,
            buildAllocationRow({
                allocationType: 'INVOICE',
                paymentDate: toDateInputValue(activeBankTransaction.bookingDate || activeBankTransaction.valueDate),
            }),
        ]);
    };

    const removeBankAllocationRow = (id) => {
        setBankAllocationRows((prev) => prev.filter((row) => row.id !== id));
        setBankAllocationErrors((prev) => {
            if (!prev?.[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const handleAllocationTypeChange = (id, allocationType) => {
        clearAllocationError(id, 'targetId');
        clearAllocationError(id, 'customerName');
        clearAllocationError(id, 'supplierName');
        setAllocationRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                const nextRow = {
                    ...row,
                    allocationType,
                };
                if (allocationType === 'NEW_INCOME') {
                    nextRow.targetId = '';
                    if (!row.newIncome.customerSearch && activeTransaction?.name) {
                        nextRow.newIncome = {
                            ...row.newIncome,
                            customerSearch: activeTransaction.name,
                        };
                    }
                }
                if (allocationType === 'NEW_EXPENSE') {
                    nextRow.targetId = '';
                    if (!row.newExpense.supplierName && activeTransaction?.name) {
                        nextRow.newExpense = {
                            ...row.newExpense,
                            supplierName: activeTransaction.name,
                        };
                    }
                }
                return nextRow;
            })
        );
    };

    const handleBankAllocationTypeChange = (id, allocationType) => {
        clearBankAllocationError(id, 'targetId');
        clearBankAllocationError(id, 'customerName');
        clearBankAllocationError(id, 'supplierName');
        setBankAllocationRows((prev) =>
            prev.map((row) => {
                if (row.id !== id) {
                    return row;
                }
                const isNewType = allocationType === 'NEW_INCOME' || allocationType === 'NEW_EXPENSE';
                const nextRow = {
                    ...row,
                    allocationType,
                };
                if (allocationType !== row.allocationType && !isNewType) {
                    nextRow.targetId = '';
                }
                if (allocationType === 'INVOICE' && !nextRow.targetId) {
                    const defaultInvoiceId = activeBankTransaction?.matchedInvoiceId
                        || referenceData.invoices?.[0]?.id
                        || '';
                    if (defaultInvoiceId) {
                        nextRow.targetId = defaultInvoiceId;
                    }
                }
                if (allocationType === 'NEW_INCOME') {
                    nextRow.targetId = '';
                    if (!row.newIncome.customerSearch && activeBankTransaction?.counterpartName) {
                        nextRow.newIncome = {
                            ...row.newIncome,
                            customerSearch: activeBankTransaction.counterpartName,
                        };
                    }
                }
                if (allocationType === 'NEW_EXPENSE') {
                    nextRow.targetId = '';
                    if (!row.newExpense.supplierName && activeBankTransaction?.counterpartName) {
                        nextRow.newExpense = {
                            ...row.newExpense,
                            supplierName: activeBankTransaction.counterpartName,
                        };
                    }
                }
                return nextRow;
            })
        );
    };

    const handleCustomerSelection = (id, value, updateNested = updateAllocationNested) => {
        if (!value) {
            updateNested(id, 'newIncome', { customerId: '', customerName: '', customerSearch: '' });
            return;
        }
        updateNested(id, 'newIncome', {
            customerId: value.id || '',
            customerName: value.displayName || value.label || '',
            customerSearch: value.displayName || value.label || '',
        });
    };

    const handleCustomerInput = (id, value, reason, updateNested = updateAllocationNested) => {
        if (reason === 'clear') {
            updateNested(id, 'newIncome', { customerId: '', customerName: '', customerSearch: '' });
            return;
        }
        if (reason === 'input') {
            updateNested(id, 'newIncome', { customerId: '', customerName: '', customerSearch: value });
        }
    };

    const handleSupplierSelection = (id, value, updateNested = updateAllocationNested) => {
        if (!value) {
            updateNested(id, 'newExpense', { supplierId: '', supplierName: '' });
            return;
        }
        updateNested(id, 'newExpense', {
            supplierId: value.id || '',
            supplierName: value.displayName || value.label || '',
        });
    };

    const handleSupplierInput = (id, value, reason, updateNested = updateAllocationNested) => {
        if (reason === 'clear') {
            updateNested(id, 'newExpense', { supplierId: '', supplierName: '' });
            return;
        }
        if (reason === 'input') {
            updateNested(id, 'newExpense', { supplierId: '', supplierName: value });
        }
    };

    const handleCreateCustomer = async (id) => {
        const row = allocationRows.find((entry) => entry.id === id);
        const name = row?.newIncome?.customerSearch?.trim();
        if (!name) {
            setAssignError('Bitte Kundennamen eingeben.');
            setAllocationRowError(id, 'customerName', 'Bitte Kundennamen eingeben.');
            return;
        }
        const matched = findMatchingCustomerOption(customerOptions, name);
        if (matched) {
            setAssignError('');
            updateAllocationNested(id, 'newIncome', {
                customerId: matched.id,
                customerName: matched.displayName,
                customerSearch: matched.displayName,
            });
            return;
        }
        setCreatingCustomer(true);
        setAssignError('');
        try {
            const payload = buildCustomerPayload(name);
            const res = await createCustomer(payload);
            const created = res?.data;
            if (!created?.id) {
                throw new Error('missing customer');
            }
            appendCustomer(created);
            const displayName = buildCustomerDisplayName(created);
            updateAllocationNested(id, 'newIncome', {
                customerId: created.id,
                customerName: displayName,
                customerSearch: displayName,
            });
        } catch (err) {
            setAssignError('Kunde konnte nicht angelegt werden.');
        } finally {
            setCreatingCustomer(false);
        }
    };

    const handleCreateSupplier = async (id) => {
        const row = allocationRows.find((entry) => entry.id === id);
        const name = row?.newExpense?.supplierName?.trim();
        if (!name) {
            setAssignError('Bitte Lieferantennamen eingeben.');
            setAllocationRowError(id, 'supplierName', 'Bitte Lieferantennamen eingeben.');
            return;
        }
        const matched = findMatchingSupplierOption(supplierOptions, name);
        if (matched) {
            setAssignError('');
            updateAllocationNested(id, 'newExpense', {
                supplierId: matched.id,
                supplierName: matched.displayName,
            });
            return;
        }
        setCreatingSupplier(true);
        setAssignError('');
        try {
            const payload = buildSupplierPayload(name);
            const res = await createSupplier(payload);
            const created = res?.data;
            if (!created?.id) {
                throw new Error('missing supplier');
            }
            appendSupplier(created);
            const displayName = buildSupplierDisplayName(created);
            updateAllocationNested(id, 'newExpense', {
                supplierId: created.id,
                supplierName: displayName,
            });
        } catch (err) {
            setAssignError('Lieferant konnte nicht angelegt werden.');
        } finally {
            setCreatingSupplier(false);
        }
    };

    const handleBankCreateCustomer = async (id) => {
        const row = bankAllocationRows.find((entry) => entry.id === id);
        const name = row?.newIncome?.customerSearch?.trim();
        if (!name) {
            setBankAssignError('Bitte Kundennamen eingeben.');
            setBankAllocationRowError(id, 'customerName', 'Bitte Kundennamen eingeben.');
            return;
        }
        const matched = findMatchingCustomerOption(customerOptions, name);
        if (matched) {
            setBankAssignError('');
            updateBankAllocationNested(id, 'newIncome', {
                customerId: matched.id,
                customerName: matched.displayName,
                customerSearch: matched.displayName,
            });
            return;
        }
        setBankCreatingCustomer(true);
        setBankAssignError('');
        try {
            const payload = buildCustomerPayload(name);
            const res = await createCustomer(payload);
            const created = res?.data;
            if (!created?.id) {
                throw new Error('missing customer');
            }
            appendCustomer(created);
            const displayName = buildCustomerDisplayName(created);
            updateBankAllocationNested(id, 'newIncome', {
                customerId: created.id,
                customerName: displayName,
                customerSearch: displayName,
            });
        } catch (err) {
            setBankAssignError('Kunde konnte nicht angelegt werden.');
        } finally {
            setBankCreatingCustomer(false);
        }
    };

    const handleBankCreateSupplier = async (id) => {
        const row = bankAllocationRows.find((entry) => entry.id === id);
        const name = row?.newExpense?.supplierName?.trim();
        if (!name) {
            setBankAssignError('Bitte Lieferantennamen eingeben.');
            setBankAllocationRowError(id, 'supplierName', 'Bitte Lieferantennamen eingeben.');
            return;
        }
        const matched = findMatchingSupplierOption(supplierOptions, name);
        if (matched) {
            setBankAssignError('');
            updateBankAllocationNested(id, 'newExpense', {
                supplierId: matched.id,
                supplierName: matched.displayName,
            });
            return;
        }
        setBankCreatingSupplier(true);
        setBankAssignError('');
        try {
            const payload = buildSupplierPayload(name);
            const res = await createSupplier(payload);
            const created = res?.data;
            if (!created?.id) {
                throw new Error('missing supplier');
            }
            appendSupplier(created);
            const displayName = buildSupplierDisplayName(created);
            updateBankAllocationNested(id, 'newExpense', {
                supplierId: created.id,
                supplierName: displayName,
            });
        } catch (err) {
            setBankAssignError('Lieferant konnte nicht angelegt werden.');
        } finally {
            setBankCreatingSupplier(false);
        }
    };

    const handleAssign = async () => {
        if (!activeTransaction) {
            return;
        }
        setAssignError('');
        if (allocationRows.length === 0) {
            setAssignError('Bitte Zuordnung erfassen.');
            return;
        }
        setAllocationErrors({});
        const transactionAmount = Math.abs(Number(activeTransaction.netAmount ?? 0));
        const validationErrors = {};
        let total = 0;
        allocationRows.forEach((row) => {
            const rowErrors = {};
            const amount = parseAmount(row.amount);
            if (!amount || amount <= 0) {
                rowErrors.amount = 'Bitte gültigen Betrag eingeben.';
            } else {
                total += amount;
            }
            if (row.allocationType === 'NEW_INCOME') {
                if (!row.newIncome.customerId) {
                    rowErrors.customerName = 'Bitte Kunden auswaehlen.';
                }
            } else if (row.allocationType === 'NEW_EXPENSE') {
                const supplierName = row.newExpense.supplierName?.trim();
                if (!supplierName && !row.newExpense.supplierId) {
                    rowErrors.supplierName = 'Bitte Lieferanten angeben.';
                }
            } else if (!row.targetId) {
                rowErrors.targetId = 'Bitte Ziel auswählen.';
            }
            if (Object.keys(rowErrors).length > 0) {
                validationErrors[row.id] = rowErrors;
            }
        });

        if (Object.keys(validationErrors).length > 0) {
            setAllocationErrors(validationErrors);
            setAssignError('Bitte markierte Felder prüfen.');
            return;
        }

        if (Math.abs(total - transactionAmount) > 0.01) {
            const sumErrors = {};
            allocationRows.forEach((row) => {
                sumErrors[row.id] = { amount: 'Summe stimmt nicht.' };
            });
            setAllocationErrors(sumErrors);
            setAssignError('Die Summe der Zuordnungen stimmt nicht mit dem Betrag des Vorgangs überein.');
            return;
        }

        setAssigning(true);

        try {
            const currency = activeTransaction.currency || 'EUR';
            const transactionDate = toDateInputValue(activeTransaction.transactionDateTime);

            const prepared = [];
            for (const row of allocationRows) {
                const amount = parseAmount(row.amount);
                const allocationType = row.allocationType;
                let targetId = row.targetId;

                if (allocationType === 'NEW_INCOME') {
                    const payload = buildIncomePayload(row.newIncome, activeTransaction, amount, transactionDate);
                    const res = await createIncome(payload);
                    targetId = res?.data?.id;
                } else if (allocationType === 'NEW_EXPENSE') {
                    const payload = buildExpensePayload(row.newExpense, activeTransaction, amount, transactionDate);
                    const res = await createExpense(payload);
                    targetId = res?.data?.id;
                }

                if (!targetId) {
                    throw new Error('Bitte Ziel fuer jede Zuordnung auswählen.');
                }

                prepared.push({
                    allocationType: allocationType === 'NEW_INCOME'
                        ? 'INCOME'
                        : allocationType === 'NEW_EXPENSE'
                            ? 'EXPENSE'
                            : allocationType,
                    targetId,
                    amount,
                    currency,
                    paymentDate: row.paymentDate || transactionDate,
                });
            }

            await allocatePaymentProviderTransaction(activeTransaction.id, { allocations: prepared });
            await refresh();
            closeAssignDialog();
        } catch (err) {
            const message = err instanceof Error && err.message ? err.message : 'Zuordnung fehlgeschlagen.';
            setAssignError(message);
        } finally {
            setAssigning(false);
        }
    };

    const handleBankAssign = async () => {
        if (!activeBankTransaction) {
            return;
        }
        if (bankAllocationRows.length === 0) {
            setBankAssignError('Bitte Zuordnung erfassen.');
            return;
        }
        setBankAssignError('');
        setBankAllocationErrors({});
        const transactionAmountFromActiveBankTransaction = Number(activeBankTransaction.amount ?? 0);
        let totalAllocatedSigned = 0;
        const validationErrors = {};
        bankAllocationRows.forEach((row) => {
            const rowErrors = {};
            const amount = parseAmount(row.amount);
            if (!amount || amount <= 0) {
                rowErrors.amount = 'Bitte gültigen Betrag eingeben.';
            } else {
                const isExpenseType = row.allocationType === 'EXPENSE' || row.allocationType === 'NEW_EXPENSE';
                totalAllocatedSigned += isExpenseType ? -amount : amount;
            }
            if (row.allocationType === 'NEW_INCOME') {
                if (!row.newIncome.customerId) {
                    rowErrors.customerName = 'Bitte Kunden auswaehlen.';
                }
            } else if (row.allocationType === 'NEW_EXPENSE') {
                const supplierName = row.newExpense.supplierName?.trim();
                if (!supplierName && !row.newExpense.supplierId) {
                    rowErrors.supplierName = 'Bitte Lieferanten angeben.';
                }
            } else if (!row.targetId) {
                rowErrors.targetId = 'Bitte Ziel auswählen.';
            }
            if (Object.keys(rowErrors).length > 0) {
                validationErrors[row.id] = rowErrors;
            }
        });

        if (Object.keys(validationErrors).length > 0) {
            setBankAllocationErrors(validationErrors);
            setBankAssignError('Bitte markierte Felder prüfen.');
            return;
        }

        if (Math.abs(totalAllocatedSigned - transactionAmountFromActiveBankTransaction) > 0.01) {
            const sumErrors = {};
            bankAllocationRows.forEach((row) => {
                sumErrors[row.id] = { amount: 'Summe stimmt nicht.' };
            });
            setBankAllocationErrors(sumErrors);
            setBankAssignError('Die Summe der Zuordnungen stimmt nicht mit dem Betrag des Vorgangs überein.');
            return;
        }

        setBankAssigning(true);
        try {
            const currency = activeBankTransaction.currency || 'EUR';
            const transactionDate = toDateInputValue(activeBankTransaction.bookingDate || activeBankTransaction.valueDate);

            const prepared = [];
            for (const row of bankAllocationRows) {
                const amount = parseAmount(row.amount);
                let allocationType = row.allocationType;
                let targetId = row.targetId;

                if (allocationType === 'NEW_INCOME') {
                    const payload = buildIncomePayload(
                        row.newIncome,
                        activeBankTransaction,
                        amount,
                        row.paymentDate || transactionDate
                    );
                    const res = await createIncome(payload);
                    targetId = res?.data?.id;
                    allocationType = 'INCOME';
                } else if (allocationType === 'NEW_EXPENSE') {
                    const payload = buildExpensePayload(
                        row.newExpense,
                        activeBankTransaction,
                        amount,
                        row.paymentDate || transactionDate,
                        'Bank'
                    );
                    const res = await createExpense(payload);
                    targetId = res?.data?.id;
                    allocationType = 'EXPENSE';
                }

                if (!targetId) {
                    throw new Error('Bitte Ziel fuer jede Zuordnung auswählen.');
                }

                let amountToSend = amount; // amount from parseAmount is always positive
                if (allocationType === 'EXPENSE') {
                    amountToSend = -amountToSend; // Negate only for sending to backend
                }

                prepared.push({
                    allocationType,
                    targetId,
                    amount: amountToSend,
                    currency,
                    paymentDate: row.paymentDate || transactionDate,
                });
            }

            await allocateBankTransaction(activeBankTransaction.id, {
                allocations: prepared,
                comment: bankComment || undefined,
            });
            await refresh();
            closeBankAssignDialog();
        } catch (err) {
            const message = err instanceof Error && err.message ? err.message : 'Zuordnung fehlgeschlagen.';
            setBankAssignError(message);
        } finally {
            setBankAssigning(false);
        }
    };


    if (!hasAccounting) {
        return <AssignedTransactionsUpgradeNotice />;
    }

    return (
        <Box>
            <AssignedTransactionsHeader
                onRefresh={refresh}
                isRefreshing={bankLoading || paymentLoading}
            />

            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Stack spacing={3}>
                    <AssignedBankTransactionsCard
                        transactions={bankTransactions}
                        loading={bankLoading}
                        error={bankError}
                        onEdit={openBankAssignDialog}
                    />
                    <AssignedPaymentTransactionsCard
                        transactions={paymentTransactions}
                        loading={paymentLoading}
                        error={paymentError}
                        onEdit={openAssignDialog}
                    />
                </Stack>
            </Container>

            <PaymentAssignDialog
                open={assignDialogOpen}
                onClose={closeAssignDialog}
                onAssign={handleAssign}
                assigning={assigning}
                transaction={activeTransaction}
                allocationRows={allocationRows}
                allocationErrors={allocationErrors}
                assignError={assignError}
                referenceLoading={referenceLoading}
                referenceData={referenceData}
                customerOptions={customerOptions}
                supplierOptions={supplierOptions}
                creatingCustomer={creatingCustomer}
                creatingSupplier={creatingSupplier}
                onAddRow={addAllocationRow}
                onRemoveRow={removeAllocationRow}
                onAllocationTypeChange={handleAllocationTypeChange}
                onAllocationRowChange={updateAllocationRow}
                onAllocationNestedChange={updateAllocationNested}
                onCustomerSelection={handleCustomerSelection}
                onCustomerInput={handleCustomerInput}
                onSupplierSelection={handleSupplierSelection}
                onSupplierInput={handleSupplierInput}
                onCreateCustomer={handleCreateCustomer}
                onCreateSupplier={handleCreateSupplier}
            />

            <BankAssignDialog
                open={bankAssignDialogOpen}
                onClose={closeBankAssignDialog}
                onAssign={handleBankAssign}
                assigning={bankAssigning}
                transaction={activeBankTransaction}
                allocationRows={bankAllocationRows}
                allocationErrors={bankAllocationErrors}
                assignError={bankAssignError}
                referenceLoading={referenceLoading}
                referenceData={referenceData}
                customerOptions={customerOptions}
                supplierOptions={supplierOptions}
                creatingCustomer={bankCreatingCustomer}
                creatingSupplier={bankCreatingSupplier}
                onAddRow={addBankAllocationRow}
                onRemoveRow={removeBankAllocationRow}
                onAllocationTypeChange={handleBankAllocationTypeChange}
                onAllocationRowChange={updateBankAllocationRow}
                onAllocationNestedChange={updateBankAllocationNested}
                onCustomerSelection={(id, value) => handleCustomerSelection(id, value, updateBankAllocationNested)}
                onCustomerInput={(id, value, reason) => handleCustomerInput(id, value, reason, updateBankAllocationNested)}
                onSupplierSelection={(id, value) => handleSupplierSelection(id, value, updateBankAllocationNested)}
                onSupplierInput={(id, value, reason) => handleSupplierInput(id, value, reason, updateBankAllocationNested)}
                onCreateCustomer={handleBankCreateCustomer}
                onCreateSupplier={handleBankCreateSupplier}
                bankComment={bankComment}
                onBankCommentChange={setBankComment}
            />
        </Box>
    );
}
